/**
 * Canonical email-provider identity — the SINGLE source of truth for "which
 * provider is this connection" across every email chat/MCP tool and the provider
 * fetch layer. See ENUM_VALUE_CONSTANTS.
 *
 * Why this exists: legacy rows report a connection's `provider` as its provider
 * TEMPLATE slug (Outlook → "outlook"), while our provider factory, MessageFetcher,
 * and tool schemas key on the integration CONFIG key (Outlook → "outlook-oauth").
 * The two are NOT equal for Outlook (they happen to coincide for Gmail, which is
 * why Gmail "just worked"). That mismatch was independently re-implemented — and
 * diverged — in ~6 places (list_emails / get_email_details / list_email_folders /
 * manage_email guards, the provider factory, plus three alias maps), so Outlook
 * connections were silently dropped by the Meridian email tools. Normalize once,
 * here, and route every membership guard + dispatch through it.
 */
import type { ProviderKey } from "@/lib/integrations/email-providers/types/common-types";

/**
 * Canonical integration CONFIG keys for email providers — the values the
 * provider factory, MessageFetcher/MessageDetailFetcher, and the provider proxy
 * actually key on. Typed against ProviderKey so a rename breaks the build.
 */
export const EMAIL_PROVIDER_CONFIG_KEYS = [
  "google-mail",
  "outlook-oauth",
  "professional-email",
] as const satisfies readonly ProviderKey[];

export type EmailProviderConfigKey = (typeof EMAIL_PROVIDER_CONFIG_KEYS)[number];

/**
 * The accepted `provider` values for a Meridian email-send tool schema — the
 * canonical config keys PLUS the two human aliases the model naturally reaches
 * for ("gmail"/"outlook"). Constraining a tool's `provider` field to this tuple
 * (z.enum) stops the model inventing values like "email"/"google"/a display name
 * that match zero connections and fail the send. Every accepted value normalizes
 * cleanly through {@link normalizeToProviderConfigKey}. Keep this the ONE source
 * a tool schema references — do not re-list provider strings inline.
 */
export const SEND_EMAIL_PROVIDER_INPUTS = [
  "google-mail",
  "outlook-oauth",
  "professional-email",
  "gmail",
  "outlook",
] as const;

export type SendEmailProviderInput = (typeof SEND_EMAIL_PROVIDER_INPUTS)[number];

/**
 * Every provider alias we may receive — the legacy provider-template slug
 * ("outlook"/"gmail"/"slack"), a legacy UI key, or the canonical config key
 * itself — mapped to its canonical config key. Folds the three previously
 * duplicated maps: PROVIDER_MAP (lib/hooks/api/messages/constants),
 * legacyProviderMap (lib/config/providers), and mapProviderToConfigKey
 * (lib/email-sync/connections/connection-manager-providers).
 */
const PROVIDER_ALIAS_TO_CONFIG_KEY: Record<string, ProviderKey> = {
  // Gmail — the legacy template slug already equals the config key.
  gmail: "google-mail",
  "google-mail": "google-mail",
  // Outlook — legacy rows carry the template slug "outlook"; our config key is "outlook-oauth".
  outlook: "outlook-oauth",
  "outlook-oauth": "outlook-oauth",
  // Slack (not email, but shares the connection namespace).
  slack: "slack-oauth",
  "slack-oauth": "slack-oauth",
  // WTC internal mail bridge (has no OAuth connection; injected by ConnectionManager).
  "professional-email": "professional-email",
};

/**
 * Normalize any provider alias / legacy provider-template slug to its canonical config key.
 * Prefers an explicit `providerConfigKey` (it is already the config key), then
 * the raw provider slug, and falls back to the input unchanged for anything we
 * don't recognize (so unknown providers surface as themselves, not silently
 * remapped).
 */
export function normalizeToProviderConfigKey(
  provider: string,
  providerConfigKey?: string | null
): string {
  const configKey = providerConfigKey?.trim().toLowerCase();
  if (configKey && PROVIDER_ALIAS_TO_CONFIG_KEY[configKey]) {
    return PROVIDER_ALIAS_TO_CONFIG_KEY[configKey];
  }
  const slug = provider.trim().toLowerCase();
  return PROVIDER_ALIAS_TO_CONFIG_KEY[slug] ?? slug;
}

/** Runtime type guard: is a normalized key one of the canonical email config keys? */
export function isEmailProviderConfigKey(value: string): value is EmailProviderConfigKey {
  return EMAIL_PROVIDER_CONFIG_KEYS.some((key) => key === value);
}

/** The minimum a connection must expose for provider identification. */
export interface ProviderIdentifiedConnection {
  provider: string;
  providerConfigKey?: string | null;
}

/**
 * Canonical config key for a connection — safe to pass to the provider factory,
 * MessageFetcher, MessageDetailFetcher, or MessageActionHandler. Resolves the
 * legacy provider-template slug ("outlook") to the config key ("outlook-oauth").
 */
export function resolveConnectionProviderKey(conn: ProviderIdentifiedConnection): string {
  return normalizeToProviderConfigKey(conn.provider, conn.providerConfigKey);
}

/**
 * Does this connection belong to one of the given canonical email providers?
 * Normalizes the connection's provider first, so a the provider "outlook" slug matches
 * the "outlook-oauth" config key.
 */
export function connectionMatchesEmailProviders(
  conn: ProviderIdentifiedConnection,
  allowed: readonly EmailProviderConfigKey[]
): boolean {
  const key = resolveConnectionProviderKey(conn);
  return allowed.some((allowedKey) => allowedKey === key);
}

/**
 * Providers whose inbox the read tools (list_emails, get_email_details,
 * list_email_folders) can list. Deliberate subset of EMAIL_PROVIDER_CONFIG_KEYS
 * (professional-email has its own inbox bridge and is not listable here).
 */
export const LISTABLE_EMAIL_PROVIDER_KEYS = [
  "google-mail",
  "outlook-oauth",
] as const satisfies readonly EmailProviderConfigKey[];

/**
 * Providers whose inbox manage_email can organize — adds the WTC
 * professional-email bridge on top of the listable set.
 */
export const MANAGEABLE_EMAIL_PROVIDER_KEYS = [
  "google-mail",
  "outlook-oauth",
  "professional-email",
] as const satisfies readonly EmailProviderConfigKey[];

/**
 * Providers that support creating a genuine PROVIDER-SIDE draft — a real
 * message saved somewhere a human can find, edit, and send later.
 * `professional-email` DOES have a Drafts folder (backed by DynamoDB, reached
 * over the same shared-secret /api/hub/inbox bridge `MANAGEABLE_EMAIL_PROVIDER_KEYS`
 * already uses for send/move/star/read) — it's listed here on purpose,
 * unlike {@link LISTABLE_EMAIL_PROVIDER_KEYS}, which covers a different,
 * provider-specific capability this provider genuinely lacks.
 */
export const DRAFTABLE_EMAIL_PROVIDER_KEYS = [
  "google-mail",
  "outlook-oauth",
  "professional-email",
] as const satisfies readonly EmailProviderConfigKey[];

/** Runtime type guard: does this normalized key support creating a draft? */
export function isDraftableEmailProviderKey(value: string): value is EmailProviderConfigKey {
  return DRAFTABLE_EMAIL_PROVIDER_KEYS.some((key) => key === value);
}

/**
 * The accepted `provider` values for the create_email_draft/list_email_drafts/
 * update_email_draft tool schemas — mirrors {@link SEND_EMAIL_PROVIDER_INPUTS},
 * now including `professional-email` (see {@link DRAFTABLE_EMAIL_PROVIDER_KEYS}).
 */
export const CREATE_EMAIL_DRAFT_PROVIDER_INPUTS = [
  "google-mail",
  "outlook-oauth",
  "professional-email",
  "gmail",
  "outlook",
] as const;

export type CreateEmailDraftProviderInput = (typeof CREATE_EMAIL_DRAFT_PROVIDER_INPUTS)[number];

/**
 * Providers that implement the mailbox-ORGANIZE capability: repairing rules,
 * reshaping folders, and replaying a rule over mail that already arrived
 * (see `MailboxOrganizeProvider`).
 *
 * A deliberate subset, not an oversight. `professional-email` is excluded for the
 * same reason it is excluded from {@link LISTABLE_EMAIL_PROVIDER_KEYS}: the WTC
 * mail bridge exposes no listable message collection to scan and does not report
 * a rule's stored conditions, so there is nothing to replay a rule against. Tools
 * gated on this set must say that plainly rather than fail obscurely — see
 * AI_EMAIL_MANAGEMENT's cross-connector parity requirement.
 *
 * Declared here rather than inline in each tool schema so the set has ONE source
 * (see ENUM_VALUE_CONSTANTS).
 */
export const ORGANIZABLE_EMAIL_PROVIDER_KEYS = [
  "google-mail",
  "outlook-oauth",
] as const satisfies readonly EmailProviderConfigKey[];
